(() => {
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const dialogTimers = new WeakMap();
  const openDialog = (dialog, opener) => {
    if (!dialog) return;
    clearTimeout(dialogTimers.get(dialog));
    dialog._opener = opener;
    opener?.setAttribute('aria-expanded', 'true');
    dialog.dataset.state = 'opening';
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('jj-dialog-open');
    requestAnimationFrame(() => requestAnimationFrame(() => { if (dialog.open) dialog.dataset.state = 'open'; }));
  };
  const closeDialog = dialog => {
    if (!dialog.open || dialog.dataset.state === 'closing') return;
    dialog.dataset.state = 'closing';
    const finish = () => { dialog.close(); };
    if (reducedMotion()) finish();
    else dialogTimers.set(dialog, setTimeout(finish, 240));
  };

  const bindDisclosure = details => {
    if (details.dataset.jjDisclosure) return;
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    details.dataset.jjDisclosure = 'true';
    let animation;
    let expanded = details.open;
    summary.setAttribute('aria-expanded', String(expanded));
    summary.addEventListener('click', event => {
      event.preventDefault();
      const start = details.getBoundingClientRect().height;
      animation?.cancel();
      expanded = !expanded;
      summary.setAttribute('aria-expanded', String(expanded));
      if (reducedMotion()) {
        details.open = expanded;
        details.style.height = '';
        details.style.overflow = '';
        return;
      }
      details.style.height = '';
      details.style.overflow = 'hidden';
      details.open = false;
      const collapsedHeight = details.getBoundingClientRect().height;
      details.open = true;
      const end = expanded ? details.getBoundingClientRect().height : collapsedHeight;
      animation = details.animate([{height: `${start}px`}, {height: `${end}px`}], {
        duration: 320, easing: 'cubic-bezier(.2,.65,.3,1)'
      });
      animation.onfinish = () => {
        details.open = expanded;
        details.style.height = '';
        details.style.overflow = '';
        animation = null;
      };
    });
  };

  const bind = (root = document) => {
    root.querySelectorAll('[data-jj-review-gallery]').forEach(gallery => {
      if (gallery.dataset.reviewBound) return;
      gallery.dataset.reviewBound = 'true';
      const slides = () => [...gallery.querySelectorAll('[data-jj-slide], .product__media-list > .product__media:not([hidden])')];
      const thumbs = () => [...gallery.querySelectorAll('[data-jj-thumb], media-dots .product__thumbnail:not([hidden])')];
      const warm = index => {
        [index, index + 1].forEach(i => {
          const img = slides()[i]?.querySelector('img');
          if (!img) return;
          img.loading = 'eager';
          if (i === index && img.fetchPriority !== 'high') img.fetchPriority = 'auto';
        });
      };
      const fallback = img => {
        if (!img || img.dataset.jjRetried || !img.hasAttribute('srcset')) return;
        img.dataset.jjRetried = 'true';
        img.removeAttribute('srcset');
        img.loading = 'eager';
      };
      gallery.addEventListener('error', event => {
        if (event.target.matches('img[data-jj-gallery-image]')) fallback(event.target);
      }, true);
      gallery.querySelectorAll('img[data-jj-gallery-image]').forEach(img => {
        if (img.complete && !img.naturalWidth) fallback(img);
      });
      const update = () => {
        const list = thumbs();
        const current = Math.max(0, list.findIndex(b => b.getAttribute('aria-current') === 'true'));
        const count = gallery.querySelector('[data-jj-review-count]');
        const text = `${current + 1} / ${list.length}`;
        if (count && list.length && count.textContent !== text) count.textContent = text;
        slides().forEach((slide, index) => {
          const button = slide.querySelector('[data-jj-image-open]');
          if (!button) return;
          button.tabIndex = index === current ? 0 : -1;
          button.setAttribute('aria-label', `Enlarge image ${index + 1}`);
        });
        warm(current);
      };
      update();
      new MutationObserver(update).observe(gallery, {subtree:true, childList:true, attributes:true, attributeFilter:['aria-current','hidden']});
      gallery.addEventListener('click', event => {
        const thumb = event.target.closest('[data-jj-thumb], .product__thumbnail');
        if (thumb) warm(thumbs().indexOf(thumb));
        if (event.target.closest('[data-jj-next], [is="next-button"]')) {
          warm(Math.max(0, thumbs().findIndex(b => b.getAttribute('aria-current') === 'true')) + 1);
        }
      }, true);
      let pointerStart;
      gallery.addEventListener('pointerdown', e => { pointerStart = [e.clientX, e.clientY]; }, {passive:true});
      gallery.addEventListener('click', event => {
        const button = event.target.closest('[data-jj-image-open]');
        if (!button) return;
        if (event.detail && pointerStart && Math.hypot(event.clientX - pointerStart[0], event.clientY - pointerStart[1]) > 10) return;
        const img = button.parentElement.querySelector('img');
        const dialog = document.getElementById('JJReviewImage');
        if (!img || !dialog) return;
        const preview = dialog.querySelector('img');
        preview.src = img.src;
        preview.alt = img.alt;
        preview.width = img.width;
        preview.height = img.height;
        openDialog(dialog, button);
      });
    });
    root.querySelectorAll('[data-jj-open-dialog]').forEach(button => {
      if (button.dataset.dialogBound) return;
      button.dataset.dialogBound = 'true';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', button.dataset.jjOpenDialog);
      button.addEventListener('click', () => openDialog(document.getElementById(button.dataset.jjOpenDialog), button));
    });
    root.querySelectorAll('.jj-review-dialog').forEach(dialog => {
      if (dialog.dataset.closeBound) return;
      dialog.dataset.closeBound = 'true';
      dialog.querySelector('[data-jj-close-dialog]')?.addEventListener('click', () => closeDialog(dialog));
      dialog.addEventListener('cancel', event => { event.preventDefault(); closeDialog(dialog); });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
      });
      dialog.addEventListener('close', () => {
        clearTimeout(dialogTimers.get(dialog));
        if (!document.querySelector('.jj-review-dialog[open]')) document.documentElement.classList.remove('jj-dialog-open');
        dialog._opener?.setAttribute('aria-expanded', 'false');
        dialog._opener?.focus({preventScroll:true});
      });
    });
    root.querySelectorAll('.jj-story-detail, .jj-customer-label, .jj-review-dialog details, .jj-evergreen details').forEach(bindDisclosure);
    root.querySelectorAll('[data-jj-smooth][href^="#"]').forEach(link => {
      if (link.dataset.jjSmoothBound) return;
      link.dataset.jjSmoothBound = 'true';
      link.addEventListener('click', event => {
        const target = document.getElementById(link.hash.slice(1));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({behavior: reducedMotion() ? 'auto' : 'smooth', block:'start'});
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bind(), {once:true}); else bind();
  document.addEventListener('shopify:section:load', e => bind(e.target));
})();
